import 'react-loading-skeleton/dist/skeleton.css';

import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import classNames from 'classnames';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useMemo, useState } from 'react';

import CustomImage from '@/components/common/customImage';
import Badge from '@/components/common/memulItem/badge';
import {
  APT_TYPE,
  BUILDING_TYPE,
  BuildingTypeKey,
  BUSINESS_DIV_TYPE,
  OFFICETEL_TYPE,
  REDEVELOP_TYPE,
} from '@/const/buildingType';
import { MemulItemType, SameMemulItemType } from '@/service/commonType';
import { useFavoriteMemul } from '@/service/memul/favoritesUtil';
import { useMemulSameList } from '@/service/memul/list/queries';
import useMobileStore from '@/store/mobileStore';
import { useSpaceStore } from '@/store/peyong';
import { handleSendGAEvent } from '@/utils/google';
import { meterToPeyong, numberToNormalPrice } from '@/utils/js/number';

export function CommonMemulItem({
  itemData,
  type = 'singleMemul',
  isSubItem,
  pageCheck,
}: {
  itemData: MemulItemType | SameMemulItemType;
  type?: 'singleMemul' | 'sameMemul';
  isSubItem?: boolean;
  pageCheck?: 'memulList' | 'danjiDetail' | 'realtor' | 'similar';
}) {
  const { handleFavoriteClick } = useFavoriteMemul();
  const { isSquareMeter } = useSpaceStore();
  const { isWebView } = useMobileStore();

  const handlePriceView = (itemData: MemulItemType | SameMemulItemType) => {
    const priceView = (priceType: string) => {
      if (
        'sameYn' in itemData &&
        pageCheck !== 'realtor' &&
        pageCheck !== 'similar' &&
        itemData.sameInfo
      ) {
        const upperCaseFirstString = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);
        // const prices = itemData.sameInfo.map((el) => el[priceType]);
        const minValue =
          itemData.sameInfo[
            ('min' + upperCaseFirstString(priceType)) as 'minDepositPrice' | 'minTradePrice'
          ];
        const maxValue =
          itemData.sameInfo[
            ('max' + upperCaseFirstString(priceType)) as 'maxDepositPrice' | 'maxTradePrice'
          ];
        return `${numberToNormalPrice({ value: minValue! })}${maxValue !== minValue ? ` <span class="unit">~</span> ${numberToNormalPrice({ value: maxValue! })}` : ''}`;
      } else {
        return `${numberToNormalPrice({ value: itemData[priceType as 'depositPrice' | 'tradePrice'] })}`;
      }
    };

    if (itemData.businessDiv === 'M' || itemData.businessDiv === 'T') {
      return `${priceView('depositPrice')} <span class="unit">/</span> ${priceView('tradePrice')}`;
    } else {
      return priceView('tradePrice');
    }
  };

  const handleScale = (itemData: MemulItemType) => {
    if (
      (['E01', 'E04', 'E03'].includes(itemData.buildingType) && itemData.businessDiv === 'S') ||
      ['K01'].includes(itemData.buildingType)
    ) {
      return `${meterToPeyong(isSquareMeter, +itemData.meter1, itemData.ptp)}/${meterToPeyong(isSquareMeter, +itemData.meter2)}${isSquareMeter ? '㎡' : '평'}`;
    }

    if (
      ['G01', 'D01'].includes(itemData.buildingType) ||
      (['E01', 'E04', 'E03'].includes(itemData.buildingType) && itemData.businessDiv !== 'S')
    ) {
      return `${meterToPeyong(isSquareMeter, itemData.meter1, itemData.ptp)}${isSquareMeter ? '㎡' : '평'}`;
    }

    return `${meterToPeyong(isSquareMeter, itemData.meter1, itemData.ptp)}/${meterToPeyong(isSquareMeter, itemData.meter2)}${isSquareMeter ? '㎡' : '평'}`;
  };

  const handleFloor = (itemData: MemulItemType) => {
    // 단독/다가구, 한옥주택, 상가주택 && 매매
    if (
      (['E01', 'E03'].includes(itemData.buildingType) && itemData.businessDiv === 'S') ||
      (itemData.buildingType === 'K01' && itemData.buildKind === '단독/다가구') ||
      itemData.buildingType === 'E04'
    ) {
      return (
        <p>
          {itemData.underFlr ? `B${itemData.underFlr}` : ''}
          {itemData.underFlr && itemData.groundFlr ? '~' : ''}
          {itemData.groundFlr
            ? `${itemData.groundFlr < 0 ? `B${Math.abs(itemData.groundFlr)}` : itemData.groundFlr}`
            : ''}
          층
        </p>
      );
    }
    if (itemData.buildingType === 'E02') {
      return itemData.groundFlr ? (
        itemData.groundFlr < 0 ? (
          <p>B{Math.abs(itemData.groundFlr)}층</p>
        ) : (
          <p>{itemData.groundFlr}층</p>
        )
      ) : (
        ''
      );
    }

    if (itemData.corrpFlrDiv === '2') {
      return itemData?.floorLevel ? <p>{itemData.floorLevel}층</p> : '';
    }

    return itemData.corrpFlr ? (
      <p>{itemData.corrpFlr < 0 ? 'B' + Math.abs(itemData.corrpFlr) : itemData.corrpFlr}층</p>
    ) : (
      ''
    );
  };

  const isCommonHouse = useMemo(
    () =>
      'buildingType' in itemData &&
      Object.keys({ ...APT_TYPE, ...OFFICETEL_TYPE }).includes(itemData.buildingType),
    [itemData?.buildingType],
  );

  const BuildingInfo = (itemData: MemulItemType) => (
    <>
      <div className="dot_divide">
        {itemData.buildingType !== 'K01' ? (
          <>
            <p>{handleBuildingType(itemData)}</p>
            <p className="danji_name">
              {isCommonHouse ? `${itemData.danjiNm ?? ''} ${itemData.dongNm}동` : itemData.bjdNm}
            </p>
          </>
        ) : (
          <>
            <p>
              {REDEVELOP_TYPE[itemData.buildingType]} {itemData.buildKind}
            </p>
            <p className="danji_name">{itemData.danjiNm}</p>
          </>
        )}
      </div>
      <div className="divide_box">
        <p>{handleScale(itemData)}</p>
        {handleFloor(itemData)}
        {itemData.buildingType !== 'K01' && <p>방{itemData.roomCount}</p>}
        {itemData.dWay && <p>{itemData.dWay}향</p>}
      </div>
    </>
  );

  const handleBuildingType = (itemData: MemulItemType) => {
    if (['D01', 'E01', 'E02', 'G01'].includes(itemData.buildingType)) {
      return itemData.buildKind ?? BUILDING_TYPE[itemData.buildingType as BuildingTypeKey];
    }
    return BUILDING_TYPE[itemData.buildingType as BuildingTypeKey];
  };

  const handleCareCondition = (itemData: MemulItemType) => {
    const isSaleType = itemData.businessDiv === 'S';
    const buildingTypes = ['A01', 'A02', 'B01', 'D01', 'E01', 'E02', 'E03', 'E04'];
    const isUrbanHousing =
      itemData.buildingType === 'G01' && itemData.buildKind === '도시형생활주택';

    return isSaleType && (buildingTypes.includes(itemData.buildingType) || isUrbanHousing);
  };

  return (
    <div className="memul_list_item">
      <Link
        href={`/memul/${itemData.articleCd}`}
        className="clickable"
        onClick={(e) => {
          if (type === 'sameMemul') e.preventDefault();
          if (pageCheck === 'memulList') {
            handleSendGAEvent('memul_memul_con', {
              event_category: 'memul',
              page_referrer: window.location.href,
              bjd_code: itemData.bjdCd,
              memul_type: BUILDING_TYPE[itemData.buildingType as BuildingTypeKey],
            });
          }
          if (pageCheck === 'danjiDetail') {
            handleSendGAEvent('memul_danji_detail_memul_con', {
              event_category: 'memul',
              danji_id: itemData.danjiCd,
              memul_id: itemData.articleCd,
            });
          }
          if (pageCheck === 'similar') {
            handleSendGAEvent('memul_memul_detail_recommand_memul_con', {
              event_category: 'memul',
              memul_id: itemData.articleCd,
            });
          }
          if (pageCheck === 'realtor') {
            if (isWebView) {
              handleSendGAEvent('realtor_detail_memul_con', {
                event_category: 'realtor',
                memul_id: itemData.articleCd,
              });
            } else {
              handleSendGAEvent('share_realtor_memul_con', {
                event_category: 'share',
                memul_id: itemData.articleCd,
              });
            }
          }
        }}
      >
        {type === 'singleMemul' && (
          <Badge itemData={itemData as MemulItemType} pageCheck={pageCheck} />
        )}

        <div className="item_detail d_flex">
          <div className="item_info flex1">
            <div className="price_info">
              <p
                className={classNames('type', {
                  text_pink: itemData.businessDiv === 'M', // 월세
                  text_green: itemData.businessDiv === 'Y', // 전세
                  text_blue: itemData.businessDiv === 'S', // 매매
                  text_mint: itemData.businessDiv === 'T', // 단기임대
                })}
              >
                {itemData.businessDivNm
                  ? itemData.businessDivNm
                  : BUSINESS_DIV_TYPE[itemData.businessDiv]}
              </p>
              <p
                className="price"
                dangerouslySetInnerHTML={{ __html: handlePriceView(itemData) }}
              />
              {'premiumPrice' in itemData &&
                Boolean(itemData.premiumPrice) &&
                ['C01', 'C02', 'C03'].includes(itemData.buildingType) && (
                  <p className="premium">
                    P {itemData.premiumPrice > 0 && '+'}
                    {numberToNormalPrice({ value: itemData.premiumPrice })}
                  </p>
                )}
            </div>
            {!isSubItem && BuildingInfo(itemData as MemulItemType)}

            {type === 'singleMemul' && <p className="description">{itemData.memulAd}</p>}
          </div>
          {type === 'singleMemul' &&
            itemData?.imageInfo?.length > 0 &&
            itemData?.imageInfo?.[0]?.thumbUrl && (
              <div
                className={classNames('item_photo', {
                  album: itemData?.zipPhotoType === 1,
                })}
              >
                <CustomImage
                  src={itemData?.imageInfo?.[0]?.thumbUrl}
                  alt="매물 리스트 썸네일"
                  width={88}
                  height={88}
                />
              </div>
            )}
        </div>
      </Link>

      {isWebView && type === 'singleMemul' && 'buildingType' in itemData && (
        <button
          type="button"
          onClick={() => {
            handleFavoriteClick({
              nScaleCode: (itemData as any)?.ptpCd ?? 0,
              nComplexCode: (itemData as any)?.danjiCd ?? 0,
              sOfferingSN: itemData.articleCd,
              sOfferType: itemData.businessDiv,
              buildingTypeCd: itemData.buildingType,
            });
            if (pageCheck === 'memulList') {
              handleSendGAEvent('memul_memul_like_btn', {
                event_category: 'memul',
                memul_id: itemData.articleCd,
              });
            }
            if (pageCheck === 'danjiDetail') {
              handleSendGAEvent('memul_danji_detail_memul_like_btn', {
                event_category: 'memul',
                danji_id: itemData.danjiCd,
                memul_id: itemData.articleCd,
              });
            }
            if (pageCheck === 'similar') {
              handleSendGAEvent('memul_memul_detail_recommand_memul_like_btn', {
                event_category: 'memul',
                memul_id: itemData.articleCd,
              });
            }
            if (pageCheck === 'realtor') {
              handleSendGAEvent('realtor_detail_memul_like_btn', {
                event_category: 'realtor',
                memul_id: itemData.articleCd,
              });
            }
          }}
          className={classNames('like', { active: itemData.memberFavoriteInfo[0].useYn === 'Y' })}
        >
          <span className="text_blind">관심 버튼</span>
        </button>
      )}

      {type === 'singleMemul' &&
        itemData.agencyInfos &&
        itemData.agencyNo &&
        pageCheck !== 'realtor' && (
          <Link
            href={`/realtor/${itemData.agencyNo}`}
            className="agency"
            onClick={() => {
              if (pageCheck === 'memulList') {
                handleSendGAEvent('memul_memul_realtor_btn', {
                  event_category: 'memul',
                  realtor_id: itemData.agencyNo,
                });
              }
              if (pageCheck === 'similar') {
                handleSendGAEvent('memul_memul_detail_recommand_memul_realtor_btn', {
                  event_category: 'memul',
                  memul_id: itemData.articleCd,
                  realtor_id: itemData.agencyNo,
                });
              }
              if (pageCheck === 'danjiDetail') {
                handleSendGAEvent('memul_danji_detail_memul_realtor_btn', {
                  event_category: 'memul',
                  danji_id: itemData.danjiCd,
                  memul_id: itemData.articleCd,
                  realtor_id: itemData.agencyNo,
                });
              }
            }}
          >
            <i className="icon_home"></i>
            <p className="agency_name">{itemData.agencyInfos.rname}​</p>
            <i className="icon_arrow"></i>
            {itemData.agencyInfos.sSafeRealestateAgencyYN === 'Y' &&
              handleCareCondition(itemData as MemulItemType) && (
                <i
                  className={classNames('bubble', {
                    care: itemData.agencyInfos.sSafeRealestateAgencyYN === 'Y',
                  })}
                ></i>
              )}
          </Link>
        )}
    </div>
  );
}

export default function MemulItem({
  itemData,
  page,
}: {
  itemData: MemulItemType;
  page?: 'memulList' | 'danjiDetail' | 'realtor';
}) {
  const [isOpen, setIsOpen] = useState(false);
  const pathname = usePathname();

  const { data } = useMemulSameList(itemData.sameSeq, {
    enabled: Boolean(isOpen && itemData.sameYn === 'Y' && itemData.sameInfo),
  });

  const showSameMemul =
    itemData.sameYn === 'Y' && itemData.sameInfo && !pathname.includes('/realtor');

  return (
    <>
      <CommonMemulItem
        itemData={itemData}
        type={showSameMemul ? 'sameMemul' : 'singleMemul'}
        pageCheck={page}
      />
      {showSameMemul && (
        <div className="memul_list_item">
          <Disclosure>
            <DisclosureButton className="list_open" onClick={() => setIsOpen(true)}>
              동일 매물 보기<i className="icon_arrow"></i>
            </DisclosureButton>
            {Array.isArray(data) && (
              <DisclosurePanel className="hidden_items">
                {data.map((item: SameMemulItemType, index: any) => (
                  <CommonMemulItem
                    key={item.articleCd + index}
                    itemData={{
                      ...item,
                      buildingType: itemData.buildingType,
                      bjdCd: itemData.bjdCd,
                      danjiCd: itemData.danjiCd,
                      roomCount: itemData.roomCount,
                      meter2: itemData.meter2,
                    }}
                    type="singleMemul"
                    isSubItem={true}
                    pageCheck={page}
                  />
                ))}
              </DisclosurePanel>
            )}
          </Disclosure>
        </div>
      )}
    </>
  );
}
